import React from 'react';
import { FILTERED_GREEN_BUTTON } from '../constants/colors';

interface ButtonProps {
  type?: 'button' | 'submit' | 'reset';
  children: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'outline';
  /** When true, uses rounded-full; otherwise rounded-lg. Default false for backward compatibility. */
  roundedFull?: boolean;
  /** When false, omits w-full so the button can shrink in flex layouts. Default true. */
  fullWidth?: boolean;
  className?: string;
  title?: string;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement>;
}

const Button: React.FC<ButtonProps> = ({
  type = 'button',
  children,
  disabled = false,
  onClick,
  variant = 'primary',
  roundedFull = false,
  fullWidth = true,
  className = '',
  title,
  onMouseEnter,
  onMouseLeave,
}) => {
  const baseClasses = 'px-5 py-2 font-medium transition-all duration-200 focus:outline-none';
  const roundedClass = roundedFull ? 'rounded-full' : 'rounded-lg';
  const widthClass = fullWidth ? 'w-full' : '';
  // Outline already has a green border — never add a focus ring (looks like a double border).
  const focusRing =
    variant === 'outline'
      ? 'focus:ring-0 focus-visible:ring-0 focus:ring-offset-0 ring-0 outline-none focus-visible:outline-none'
      : 'focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900';

  const variantClasses = {
    primary: FILTERED_GREEN_BUTTON,
    secondary: 'bg-[#303030] hover:bg-[#363636] text-white focus:ring-gray-500',
    outline: 'bg-[#303030] hover:bg-[#363636] border-2 border-emerald-500 text-white',
  };

  const disabledClasses = disabled ? 'opacity-70 cursor-not-allowed' : '';

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      title={title}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`${baseClasses} ${focusRing} ${roundedClass} ${widthClass} ${variantClasses[variant]} ${disabledClasses} ${className}`.trim()}
    >
      {children}
    </button>
  );
};

export default Button;
